import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'backend/auth-hooks';
import { auth, db } from './backend/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'backend/database';
import { getAuth, signOut } from 'backend/auth';
import { setAuthExpiredHandler } from './utils/authEvents';
import { UserProvider, useUser } from './contexts/UserContext';
import ErrorBoundary from './components/ErrorBoundary';
import errorHandler from './utils/errorHandler';
import logger from './utils/logger';
import InstitutionAdminGuard from './components/InstitutionAdminGuard';
import GlobalCallHandler from './components/GlobalCallHandler';

// Lazy-load layouts and heavy components to reduce initial bundle size.
// These are only needed after the user authenticates and their role is
// determined, so they don't need to be in the critical path.
const Layout = lazy(() => import('./components/Layout'));
const ClientPortalLayout = lazy(() => import('./components/ClientPortalLayout'));
const ServiceProviderLayout = lazy(() => import('./components/ServiceProviderLayout'));
const CaregiverLayout = lazy(() => import('./components/CaregiverLayout'));
const StaffMessages = lazy(() => import('./pages/StaffMessages'));
const NativeMobileHandler = lazy(() => import('./components/NativeMobileHandler'));
import LoadingSpinner from './components/LoadingSpinner';

import MobileOptimization from './components/MobileOptimization';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import VoiceCommandInterface from './components/VoiceCommandInterface';
import GestureControls from './components/GestureControls';
import Landing from './pages/Landing';
import UnifiedLogin from './pages/UnifiedLogin';
import InstitutionLanding from './pages/InstitutionLanding';

// Lazy-load other heavy components to reduce initial bundle size.
const SecuritySettings = lazy(() => import('./components/SecuritySettings'));
const SecurityDashboard = lazy(() => import('./components/SecurityDashboard'));
const ChooseInstitution = lazy(() => import('./pages/ChooseInstitution'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const TenantPartners = lazy(() => import('./pages/TenantPartners'));
// Admin routes deprecated
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Medications = lazy(() => import('./pages/Medications'));
const VitalSigns = lazy(() => import('./pages/VitalSigns'));
const Appointments = lazy(() => import('./pages/Appointments'));
const Consultation = lazy(() => import('./pages/Consultation'));
const Profile = lazy(() => import('./pages/Profile'));
const CaregiverDashboard = lazy(() => import('./pages/CaregiverDashboard'));
const CaregiverSchedule = lazy(() => import('./pages/CaregiverSchedule'));
const CaregiverClients = lazy(() => import('./pages/CaregiverPatients'));
const CaregiverTasks = lazy(() => import('./pages/CaregiverTasks'));
const CaregiverOnboarding = lazy(() => import('./pages/CaregiverOnboarding'));
const SuperAdminLicensing = lazy(() => import('./pages/SuperAdminLicensing'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const SuperAdminSettings = lazy(() => import('./pages/SuperAdminSettings'));
const SuperAdminManagement = lazy(() => import('./pages/SuperAdminManagement'));
const SuperAdminAuditLogs = lazy(() => import('./pages/SuperAdminAuditLogs'));
const SuperAdminUserManagement = lazy(() => import('./pages/SuperAdminUserManagement'));
const InstitutionAdminDashboard = lazy(() => import('./pages/InstitutionAdminDashboard'));
const InstitutionUserManagement = lazy(() => import('./pages/InstitutionUserManagement'));
const InstitutionSettings = lazy(() => import('./pages/InstitutionSettings'));
const LicenseRequired = lazy(() => import('./pages/LicenseRequired'));
const InstitutionCaregiverOnboarding = lazy(() => import('./pages/InstitutionCaregiverOnboarding'));
const InstitutionCaregiverPendingApproval = lazy(() => import('./pages/InstitutionCaregiverPendingApproval'));
const InstitutionCaregiverDashboard = lazy(() => import('./pages/InstitutionCaregiverDashboard'));
const InstitutionPharmacyDashboard = lazy(() => import('./pages/InstitutionPharmacyDashboard'));
const CaregiverNavigation = lazy(() => import('./pages/CaregiverNavigation'));
const CaregiverPhotos = lazy(() => import('./pages/CaregiverPhotos'));
const CaregiverPerformance = lazy(() => import('./pages/CaregiverPerformance'));
const CaregiverEmergency = lazy(() => import('./pages/CaregiverEmergency'));
const CaregiverSettings = lazy(() => import('./pages/CaregiverSettings'));
const Telemedicine = lazy(() => import('./pages/Telemedicine'));
const Messages = lazy(() => import('./pages/Messages'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Services = lazy(() => import('./pages/Services'));
const ClientCaregivers = lazy(() => import('./pages/PatientCaregivers'));
const Pricing = lazy(() => import('./pages/Pricing'));
// All onboarding is now integrated into Auth.js
// Standalone admin pages removed - Institution Admin is the primary admin interface
const MedicalDocuments = lazy(() => import('./pages/MedicalDocuments'));
const ClientPortalHelp = lazy(() => import('./pages/ClientPortalHelp'));
const ServiceProviderDashboard = lazy(() => import('./pages/ServiceProviderDashboard'));
const RouteOptimization = lazy(() => import('./pages/RouteOptimization'));
const WebRTCTest = lazy(() => import('./pages/WebRTCTest'));
const PatientAccount = lazy(() => import('./pages/PatientAccount'));
const InstitutionLabTechnicianDashboard = lazy(() => import('./pages/InstitutionLabTechnicianDashboard'));

// PWA and security services are loaded dynamically on first use to keep
// the initial bundle small. They are singletons, so we cache the import.
let pwaService = null;
let hapticService = null;
let voiceCommandService = null;
let gestureService = null;
let securityMonitoringService = null;
let biometricAuthService = null;
let secureConfigService = null;

const loadPwaService = () =>
  import('./services/pwaService').then((m) => { pwaService = m.default; return m.default; });
const loadHapticService = () =>
  import('./services/hapticService').then((m) => { hapticService = m.default; return m.default; });
const loadVoiceCommandService = () =>
  import('./services/voiceCommandService').then((m) => { voiceCommandService = m.default; return m.default; });
const loadGestureService = () =>
  import('./services/gestureService').then((m) => { gestureService = m.default; return m.default; });
const loadSecurityMonitoringService = () =>
  import('./services/securityMonitoringService').then((m) => { securityMonitoringService = m.default; return m.default; });
const loadBiometricAuthService = () =>
  import('./services/biometricAuthService').then((m) => { biometricAuthService = m.default; return m.default; });
const loadSecureConfigService = () =>
  import('./services/secureConfigService').then((m) => { secureConfigService = m.default; return m.default; });

// Capacitor is heavy — lazy-detect native platform
let isNativeApp = false;
try {
  // Dynamic check without importing the full Capacitor bundle
  const { Capacitor } = require('@capacitor/core');
  isNativeApp = Capacitor?.isNativePlatform?.() || false;
} catch {
  isNativeApp = false;
}

function App() {
  const navigate = useNavigate();
  const [showVoiceInterface, setShowVoiceInterface] = useState(false);
  const [showGestureControls, setShowGestureControls] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isMobile, setIsMobile] = useState(false);

  // Register global auth-expired handler so backend/database.js can signal
  // the app to sign out and redirect without performing a full page reload.
  useEffect(() => {
    setAuthExpiredHandler(() => {
      try {
        signOut(getAuth());
      } catch (e) {
        // Ignore sign-out errors during expiration handling
      }
      navigate('/login', { replace: true });
    });
  }, [navigate]);

  // PWA and Security Services Setup — loaded dynamically to keep initial bundle small
  useEffect(() => {
    let cancelled = false;

    // Dynamically import and initialize PWA + security services in parallel.
    // These are non-critical for first paint and can load after the app is interactive.
    Promise.all([
      loadPwaService(),
      loadSecurityMonitoringService(),
      loadBiometricAuthService(),
      loadSecureConfigService(),
    ])
      .then(([pwa, secMon, bio, secCfg]) => {
        if (cancelled) return;
        try {
          pwa.init();
          logger.info('PWA services initialized successfully');
          secMon.initialize();
          bio.initialize();
          secMon.logSecurityEvent('APP_INITIALIZATION', {
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            features: secCfg.getFeatureFlags()
          });
          logger.info('Security services initialized successfully');
        } catch (error) {
          errorHandler.handleError(error, { context: 'pwa_initialization' });
        }
      })
      .catch((error) => {
        // Non-critical — app still works without PWA/security services
        console.warn('PWA/security service initialization deferred:', error?.message);
      });

    // Request notification permission if available
    if ('Notification' in window && Notification.permission === 'default') {
      loadPwaService().then((pwa) => {
        if (!cancelled) {
          try { pwa.requestNotificationPermission(); } catch (e) {}
        }
      }).catch(() => {});
    }

    // Initialize web push notifications after service worker is ready.
    // Only runs on web (non-native) platforms — native uses Capacitor push.
    if (!isNativeApp && 'serviceWorker' in navigator && 'PushManager' in window) {
      import('./services/pushNotificationService').then(({ default: pushService }) => {
        if (cancelled) return;
        // Wait a tick for the service worker to be registered by pwaService
        setTimeout(() => {
          pushService.init().catch((err) => {
            console.warn('Web push init deferred:', err?.message);
          });
        }, 3000);
      }).catch(() => {});
    }
    
    // Check if mobile device
    const checkMobile = () => {
      try {
        setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
      } catch (error) {
        errorHandler.handleError(error, { context: 'mobile_detection' });
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Online/offline listeners
    const handleOnline = () => {
      setIsOnline(true);
      logger.info('App is back online');
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      logger.warn('App is offline');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      cancelled = true;
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <ErrorBoundary name="App">
      <UserProvider>
        <GlobalCallHandler />
        <AppContent
          isMobile={isMobile}
          isOnline={isOnline}
          showVoiceInterface={showVoiceInterface}
          setShowVoiceInterface={setShowVoiceInterface}
          showGestureControls={showGestureControls}
          setShowGestureControls={setShowGestureControls}
        />
      </UserProvider>
    </ErrorBoundary>
  );
}

/**
 * AppContent — handles the actual rendering based on auth state from UserContext.
 * This avoids redundant onAuthStateChanged checks in both App.js and UserProvider.
 */
function AppContent({ 
  isMobile, 
  isOnline, 
  showVoiceInterface, 
  setShowVoiceInterface, 
  showGestureControls, 
  setShowGestureControls 
}) {
  const { user, userProfile, userRole, loading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  // Super-admins are confined to the /super-admin portal — any other route
  // (including the login page while already authenticated) bounces them
  // back to their dashboard.
  const isSuperAdminUser = userRole === 'super-admin' ||
    userProfile?.userType === 'super-admin' ||
    userProfile?.user_type === 'super-admin';
  useEffect(() => {
    if (loading || !user || !isSuperAdminUser) return;
    const path = location.pathname;
    if (!path.startsWith('/super-admin') || path === '/super-admin/login') {
      navigate('/super-admin/dashboard', { replace: true });
    }
  }, [loading, user, isSuperAdminUser, location.pathname, navigate]);

  // Voice command handlers
  const handleVoiceCommand = (command, params) => {
    console.log('Voice command received:', command, params);
    
    switch (command) {
      case 'call':
        // Navigate to messages page (calls tab removed)
        window.location.href = '/service-provider/messages';
        break;
      case 'endCall':
        // End current call (implement call service integration)
        break;
      case 'answerCall':
        // Answer incoming call
        break;
      case 'rejectCall':
        // Reject incoming call
        break;
      case 'sendMessage':
        // Navigate to messages
        window.location.href = '/service-provider/messages';
        break;
      case 'readMessages':
        // Navigate to messages
        window.location.href = '/service-provider/messages';
        break;
      case 'navigate':
        // Handle navigation commands
        if (params.text) {
          const navText = params.text.toLowerCase();
          if (navText.includes('client')) {
            window.location.href = '/service-provider/medical-records';
          } else if (navText.includes('schedule')) {
            window.location.href = '/service-provider/schedule';
          } else if (navText.includes('task')) {
            window.location.href = '/service-provider/tasks';
          }
        }
        break;
      case 'back':
        // Go back
        window.history.back();
        break;
      case 'home':
        // Go to dashboard
        window.location.href = '/service-provider';
        break;
      case 'completeTask':
        // Handle task completion
        break;
      case 'assignTask':
        // Handle task assignment
        break;
      case 'emergency':
        // Trigger emergency alert
        window.location.href = '/service-provider/emergency';
        break;
      case 'medication':
        // Navigate to medications
        window.location.href = '/service-provider/prescriptions';
        break;
      case 'vitalSigns':
        // Navigate to vital signs
        window.location.href = '/service-provider/diagnostics';
        break;
      case 'help':
        // Show help
        break;
      default:
        console.log('Unknown voice command:', command);
    }
  };

  // Gesture handlers
  const handleGesture = (gesture, data) => {
    console.log('Gesture received:', gesture, data);
    
    switch (gesture) {
      case 'swipe-left':
        // Go back
        window.history.back();
        break;
      case 'swipe-right':
        // Go forward
        window.history.forward();
        break;
      case 'swipe-up':
        // Let native scroll handle vertical swipes — do not call window.scrollBy
        break;
      case 'swipe-down':
        // Let native scroll handle vertical swipes — do not call window.scrollBy
        break;
      case 'pinch-in':
        // Zoom out
        document.body.style.zoom = Math.max(0.5, parseFloat(document.body.style.zoom || 1) - 0.1);
        break;
      case 'pinch-out':
        // Zoom in
        document.body.style.zoom = Math.min(2, parseFloat(document.body.style.zoom || 1) + 0.1);
        break;
      case 'long-press':
        // Show context menu or long press action
        break;
      case 'double-tap':
        // Refresh page
        window.location.reload();
        break;
      default:
        console.log('Unknown gesture:', gesture);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={null}>
      <NativeMobileHandler>
        {/* Lazy-loaded mobile/PWA components */}
        <Suspense fallback={null}>
          <MobileOptimization />
          <PWAInstallPrompt />
          <OfflineIndicator />

          {/* Voice Command Interface */}
          <VoiceCommandInterface
            isOpen={showVoiceInterface}
            onClose={() => setShowVoiceInterface(false)}
            onCommand={handleVoiceCommand}
          />

          {/* Gesture Controls */}
          <GestureControls
            isOpen={showGestureControls}
            onClose={() => setShowGestureControls(false)}
            onGesture={handleGesture}
          />
        </Suspense>
        
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
      {/* Public routes */}
      <Route 
        path="/" 
        element={
          isNativeApp
            ? (user ? <RoleBasedDashboardRoute /> : <UnifiedLogin />)
            : <Landing />
        } 
      />
      <Route 
        path="/auth" 
        element={user ? <RoleBasedDashboardRoute /> : <UnifiedLogin />} 
      />
      <Route
        path="/login"
        element={user ? <SignInRouteHandler /> : <UnifiedLogin />}
      />
      <Route 
        path="/signup" 
        element={<Navigate to="/login" replace />} 
      />

      {/* Password reset — public; reached via the emailed reset link */}
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      
      {/* Super Admin Routes - Must be before other protected routes */}
      <Route
        path="/super-admin/login"
        element={<UnifiedLogin />}
      />

      <Route
        path="/super-admin/dashboard"
        element={<SuperAdminRoute />}
      />
      
      <Route
        path="/super-admin/licensing"
        element={<SuperAdminRoute><SuperAdminLicensing /></SuperAdminRoute>}
      />

      <Route
        path="/super-admin/settings"
        element={<SuperAdminRoute><SuperAdminSettings /></SuperAdminRoute>}
      />

      <Route
        path="/super-admin/management"
        element={<SuperAdminRoute><SuperAdminManagement /></SuperAdminRoute>}
      />

      <Route
        path="/super-admin/audit-logs"
        element={<SuperAdminRoute><SuperAdminAuditLogs /></SuperAdminRoute>}
      />

      <Route
        path="/super-admin/users"
        element={<SuperAdminRoute><SuperAdminUserManagement /></SuperAdminRoute>}
      />

      <Route
        path="/super-admin"
        element={<SuperAdminRoute />}
      />

      {/* Old institution portal route — redirect to login */}
      <Route
        path="/onboard"
        element={<Navigate to="/login" replace />}
      />

      {/* License Activation - Public */}
      <Route 
        path="/license-required" 
        element={<LicenseRequired />} 
      />
      
      {/* Legacy institution login - redirects to unified login */}
      <Route 
        path="/institution/login" 
        element={<UnifiedLogin />} 
      />

      {/* Institution Admin Routes - PROTECTED WITH GUARD */}
      <Route 
        path="/institution-admin/dashboard" 
        element={<InstitutionAdminGuard><InstitutionAdminDashboard /></InstitutionAdminGuard>} 
      />
      
      <Route
        path="/institution-admin/users"
        element={user ? <InstitutionAdminGuard><InstitutionUserManagement /></InstitutionAdminGuard> : <Navigate to="/login" replace />}
      />

      <Route
        path="/institution-admin/settings"
        element={user ? <InstitutionAdminGuard><InstitutionSettings /></InstitutionAdminGuard> : <Navigate to="/login" replace />}
      />
      
      <Route 
        path="/institution-admin" 
        element={<Navigate to="/institution-admin/dashboard" replace />} 
      />
      
      {/* Client Account - Manage Client account information */}
      <Route 
        path="/client/:clientId/account" 
        element={<PatientAccount />} 
      />
      <Route 
        path="/Client/:clientId/account" 
        element={<PatientAccount />} 
      />
      
      {/* Client Account without ID - uses logged-in user */}
      <Route 
        path="/client/account" 
        element={<PatientAccount />} 
      />
      <Route 
        path="/Client/account" 
        element={<PatientAccount />} 
      />
      
      {/* Client Dashboard / Portal */}
      <Route 
        path="/dashboard" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<Dashboard />} />
      </Route>

      <Route 
        path="/client/dashboard" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<Dashboard />} />
      </Route>

      <Route 
        path="/client" 
        element={<Navigate to="/dashboard" replace />} 
      />

      {/* Institution Caregiver Routes */}
      <Route 
        path="/institution-caregiver/onboarding" 
        element={user ? <InstitutionCaregiverOnboarding /> : <Navigate to="/login" replace />} 
      />
      
      <Route 
        path="/institution-caregiver/pending-approval" 
        element={user ? <InstitutionCaregiverPendingApproval /> : <Navigate to="/login" replace />} 
      />
      
      <Route 
        path="/institution-caregiver/dashboard" 
        element={user ? <InstitutionCaregiverDashboard /> : <Navigate to="/login" replace />} 
      />

      <Route 
        path="/institution-caregiver" 
        element={<Navigate to="/institution-caregiver/dashboard" replace />} 
      />

      {/* Pharmacy Dashboard */}
      <Route 
        path="/institution-pharmacy/dashboard" 
        element={user ? <InstitutionPharmacyDashboard /> : <Navigate to="/login" replace />} 
      />

      {/* Lab Technician Dashboard */}
      <Route 
        path="/institution-lab-technician/dashboard" 
        element={user ? <InstitutionLabTechnicianDashboard /> : <Navigate to="/login" replace />} 
      />

      {/* Institution Pharmacist Routes */}
      <Route 
        path="/institution-pharmacist/dashboard" 
        element={user ? <InstitutionPharmacyDashboard /> : <Navigate to="/login" replace />} 
      />

      <Route 
        path="/institution-pharmacist" 
        element={<Navigate to="/institution-pharmacist/dashboard" replace />} 
      />
      
      {/* Protected routes - Dashboard removed (clients don't have accounts) */}
      <Route 
        path="/medications" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<Medications />} />
      </Route>
      <Route 
        path="/vital-signs" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<VitalSigns />} />
      </Route>
      <Route 
        path="/appointments" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<Appointments />} />
      </Route>
      <Route 
        path="/telemedicine" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<Telemedicine />} />
      </Route>
      <Route 
        path="/medical-documents" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<MedicalDocuments />} />
      </Route>
      <Route 
        path="/webrtc-test" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<WebRTCTest />} />
      </Route>
      
      {/* Direct test route for caregiver onboarding - removed as onboarding is now integrated into Auth.js */}
      <Route 
        path="/profile" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<Profile />} />
      </Route>
      <Route 
        path="/messages" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<Messages />} />
      </Route>
      <Route
        path="/subscription"
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />}
      >
        <Route index element={<Subscription />} />
      </Route>
      <Route 
        path="/client-caregivers" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<ClientCaregivers />} />
      </Route>
      <Route 
        path="/security" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<SecuritySettings />} />
      </Route>
      <Route 
        path="/security-dashboard" 
        element={user ? <ClientPortalLayout /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<SecurityDashboard />} />
      </Route>
      <Route 
        path="/caregiver" 
        element={user ? <StrictCaregiverGuard /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<CaregiverDashboard />} />
        <Route path="schedule" element={<CaregiverSchedule />} />
        <Route path="tasks" element={<CaregiverTasks />} />
        <Route path="messages" element={<StaffMessages />} />
        <Route path="navigation" element={<CaregiverNavigation />} />
        <Route path="photos" element={<CaregiverPhotos />} />
        <Route path="performance" element={<CaregiverPerformance />} />
        <Route path="emergency" element={<CaregiverEmergency />} />
        <Route path="settings" element={<CaregiverSettings />} />
      </Route>

      {/* Service Provider routes (unified for doctors and caregivers) */}
      <Route 
        path="/service-provider" 
        element={user ? <ServiceProviderGuard /> : <Navigate to="/login" replace />} 
      >
        <Route index element={<ServiceProviderDashboard />} />
        <Route path="schedule" element={<CaregiverSchedule />} />
        <Route path="routes" element={<RouteOptimization />} />
        <Route path="messages" element={<StaffMessages />} />
        <Route path="tasks" element={<CaregiverTasks />} />
        <Route path="care-logs" element={<CaregiverPhotos />} />
        <Route path="photos" element={<CaregiverPhotos />} />
        <Route path="activities" element={<CaregiverPerformance />} />
        <Route path="medical-records" element={<CaregiverClients />} />
        <Route path="prescriptions" element={<Medications />} />
        <Route path="consultations" element={<Consultation />} />
        <Route path="telemedicine" element={<Telemedicine />} />
        <Route path="diagnostics" element={<VitalSigns />} />
        <Route path="emergency" element={<CaregiverEmergency />} />
        <Route path="settings" element={<CaregiverSettings />} />
      </Route>
      <Route 
        path="/services" 
        element={<Services />} 
      />
      <Route 
        path="/pricing" 
        element={<Pricing />} 
      />
      
      {/* Caregiver Login Route - consolidated to UnifiedLogin */}
      <Route 
        path="/caregiver/login" 
        element={user ? <Navigate to="/caregiver/onboarding" replace /> : <UnifiedLogin />} 
      />
      {/* Caregiver Onboarding Route */}
      <Route 
        path="/caregiver/onboarding" 
        element={user ? <CaregiverOnboarding /> : <Navigate to="/login" replace />} 
      />
      
      {/* Admin routes deprecated: redirect to institution admin */}
      <Route path="/admin/*" element={<Navigate to="/institution-admin/dashboard" replace />} />
      
      {/* Institution entry: partners page OR specific institution landing when ?institution= is present */}
      <Route path="/institution" element={<InstitutionEntry />} />

      {/* Institution chooser for multi-institution users */}
      <Route path="/choose-institution" element={<ChooseInstitution />} />

      {/* Legacy admin pages are redirected above */}
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </NativeMobileHandler>
      </Suspense>
  );
}

function InstitutionEntry() {
  const [params] = useSearchParams();
  const id = params.get('institution');
  return id ? <InstitutionLanding /> : <TenantPartners />;
}

// Super-admin route — inline guard that renders children directly
// without a separate guard component to avoid timing/context issues.
function SuperAdminRoute({ children }) {
  const { user, userProfile, userRole, loading } = useUser();

  console.log('🛡️ SuperAdminRoute:', { loading, hasUser: !!user, userRole, userType: userProfile?.userType });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading super-admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('🛡️ SuperAdminRoute: no user, redirecting to /super-admin/login');
    return <Navigate to="/super-admin/login" replace />;
  }

  const isSuperAdmin =
    userRole === 'super-admin' ||
    userProfile?.userType === 'super-admin' ||
    userProfile?.user_type === 'super-admin';

  if (!isSuperAdmin) {
    console.log('🛡️ SuperAdminRoute: not super-admin (role:', userRole, '), redirecting to /super-admin/login');
    return <Navigate to="/super-admin/login" replace />;
  }

  console.log('🛡️ SuperAdminRoute: access granted, rendering children');
  return children || <SuperAdminDashboard />;
}

// Sign-in route handler - focuses on caregiver/service provider access
function SignInRouteHandler() {
  const { userRole, userProfile, loading } = useUser();
  const navigate = useNavigate();
  const [showLoginForm, setShowLoginForm] = React.useState(false);

  // Guard against infinite redirect loops. The counter only increments when an
  // actual redirect is rendered — mounting on /login via browser back no longer
  // counts toward (or trips) the loop detector.
  const redirectTo = (to) => {
    const now = Date.now();
    const key = '__signin_redirect_count';
    const data = JSON.parse(sessionStorage.getItem(key) || '{"count":0,"ts":0}');
    const count = (now - data.ts > 10000) ? 1 : data.count + 1;
    sessionStorage.setItem(key, JSON.stringify({ count, ts: count === 1 ? now : data.ts }));

    if (count > 3) {
      console.error('🚫 Infinite redirect loop detected — clearing session');
      sessionStorage.removeItem(key);
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Also clear auth compat state so onAuthStateChanged listeners fire
      auth.currentUser = null;
      (auth.__listeners || []).forEach((cb) => cb(null));
      return <Navigate to="/login" replace />;
    }
    return <Navigate to={to} replace />;
  };

  // Show loading while user profile is being fetched
  if (loading || !userProfile) {
    return <LoadingSpinner />;
  }

  console.log('🔄 SignInRouteHandler - Checking user role:', {
    userRole,
    userType: userProfile?.userType,
    institutionId: userProfile?.institutionId,
    currentPath: window.location.pathname
  });

  // Super-admin always goes to the super-admin dashboard
  if (userRole === 'super-admin' || userProfile?.userType === 'super-admin') {
    console.log('🚀 Redirecting super-admin to /super-admin/dashboard');
    return redirectTo('/super-admin/dashboard');
  }

  // Compute the role-based dashboard path for the "continue" action below.
  const dashboardPath = (() => {
    const role = userRole || userProfile?.userType;
    const inst = userProfile?.institutionId;
    if (inst) {
      if (role === 'admin') return `/institution-admin/dashboard?institution=${inst}`;
      if (role === 'pharmacist') return `/institution-pharmacy/dashboard?institution=${inst}`;
      if (role === 'lab_technician' || role === 'lab-technician') return `/institution-lab-technician/dashboard?institution=${inst}`;
      return `/institution-caregiver/dashboard?institution=${inst}`;
    }
    if (role === 'admin') return '/admin';
    if (role === 'caregiver' || role === 'doctor' || role === 'nurse') return '/service-provider';
    return '/dashboard';
  })();

  // If the user is on /login (not coming from another route), they got here via
  // browser back or a bookmark while still authenticated. Show a stable
  // "already signed in" card instead of auto-redirecting (which ping-pongs with
  // the back button) or showing a bare login form (confusing — they're logged
  // in). They can continue to their dashboard or switch accounts.
  //
  // BUT: if a fresh login just happened (UnifiedLogin set __fresh_login before
  // calling window.location.href), redirect to the appropriate dashboard based
  // on the user data in localStorage. The page reload from window.location.href
  // resets React state, so UserContext.user is null and /dashboard would bounce
  // back to /login — we must handle the redirect here instead.
  if (window.location.pathname === '/login') {
    const freshLogin = sessionStorage.getItem('__fresh_login');
    if (freshLogin && Date.now() - parseInt(freshLogin) < 30000) {
      console.log('🔄 Fresh login detected — redirecting to dashboard from localStorage');
      sessionStorage.removeItem('__fresh_login');

      // Read user from localStorage to determine role-based redirect
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const u = JSON.parse(stored);
          const role = u.userType || u.type || u.role || (Array.isArray(u.roles) ? u.roles[0] : null);
          const instId = u.institutionId;

          if (role === 'super-admin') {
            return redirectTo('/super-admin/dashboard');
          }
          if (role === 'admin' && instId) {
            return redirectTo(`/institution-admin/dashboard?institution=${instId}`);
          }
          if (role === 'pharmacist' && instId) {
            return redirectTo(`/institution-pharmacy/dashboard?institution=${instId}`);
          }
          if ((role === 'lab_technician' || role === 'lab-technician') && instId) {
            return redirectTo(`/institution-lab-technician/dashboard?institution=${instId}`);
          }
          if ((role === 'caregiver' || role === 'doctor' || role === 'nurse') && instId) {
            if (u.onboardingComplete) {
              return redirectTo(`/institution-caregiver/dashboard?institution=${instId}&role=${role}`);
            }
            return redirectTo(`/institution-caregiver/onboarding?institution=${instId}`);
          }
          // client, patient, elderly, or unknown → client dashboard
          return redirectTo('/dashboard');
        }
      } catch (e) {
        console.warn('Failed to parse stored user for fresh login redirect:', e);
      }
      // Fallback: if we can't parse user, just go to /dashboard
      return redirectTo('/dashboard');
    }

    if (showLoginForm) {
      return <UnifiedLogin />;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">You're already signed in</h2>
          <p className="text-sm text-gray-600 mb-6">
            {userProfile?.name || userProfile?.displayName || userProfile?.email || 'Signed in'}
          </p>
          <button
            onClick={() => navigate(dashboardPath)}
            className="w-full px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 mb-3"
          >
            Continue to Dashboard
          </button>
          <button
            onClick={() => setShowLoginForm(true)}
            className="w-full px-4 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            Sign in with a different account
          </button>
        </div>
      </div>
    );
  }

  // Check if user came from institution login - if so, let them continue to their intended destination
  const urlParams = new URLSearchParams(window.location.search);
  const institutionId = urlParams.get('institution');
  const roleParam = urlParams.get('role');
  
  if (institutionId && roleParam) {
    console.log('🏢 User came from institution login, routing to appropriate dashboard:', { institutionId, roleParam });

    if (roleParam === 'admin') {
      return redirectTo(`/institution-admin/dashboard?institution=${institutionId}`);
    } else if (roleParam === 'pharmacist') {
      return redirectTo(`/institution-pharmacy/dashboard?institution=${institutionId}`);
    } else if (roleParam === 'lab_technician' || roleParam === 'lab-technician') {
      return redirectTo(`/institution-lab-technician/dashboard?institution=${institutionId}`);
    } else if (roleParam === 'caregiver') {
      return redirectTo(`/institution-caregiver/dashboard?institution=${institutionId}`);
    }
  }

  // Check if user has institution ID in their profile - route them to their institution portal
  if (userProfile?.institutionId) {
    console.log('🏢 User belongs to institution, routing to institution portal:', userProfile.institutionId);

    if (userRole === 'admin') {
      return redirectTo(`/institution-admin/dashboard?institution=${userProfile.institutionId}`);
    } else if (userRole === 'pharmacist') {
      return redirectTo(`/institution-pharmacy/dashboard?institution=${userProfile.institutionId}`);
    } else if (userRole === 'lab_technician' || userRole === 'lab-technician') {
      return redirectTo(`/institution-lab-technician/dashboard?institution=${userProfile.institutionId}`);
    } else if (userRole === 'caregiver' || userRole === 'doctor' || userRole === 'nurse') {
      return redirectTo(`/institution-caregiver/dashboard?institution=${userProfile.institutionId}`);
    }
  }

  // Check for admin session override
  const hasAdminSession = sessionStorage.getItem('Care Master_admin_session') === 'true';

  // Redirect admins to admin dashboard (standalone admins without institution)
  if ((userRole === 'admin' || hasAdminSession) && !userProfile?.institutionId) {
    console.log('🚀 Redirecting admin to /admin');
    return redirectTo('/admin');
  }

  // Redirect caregivers and doctors to service provider dashboard (standalone without institution)
  if ((userRole === 'caregiver' || userRole === 'doctor') && !userProfile?.institutionId) {
    console.log('🚀 Redirecting user to /service-provider (caregiver portal)');
    return redirectTo('/service-provider');
  }

  // For other users (clients, elderly), show message that this is for caregivers
  console.log('✅ Client/elderly accessing caregiver portal, redirecting to home');
  return redirectTo('/');
}

// Role-based dashboard routing component
function RoleBasedDashboardRoute() {
  const { userRole, userProfile, loading } = useUser();
  
  // Show loading while user profile is being fetched
  if (loading || !userProfile) {
    return <LoadingSpinner />;
  }
  
  console.log('🔄 RoleBasedDashboardRoute - Checking user role:', {
    userRole,
    userType: userProfile?.userType,
    institutionId: userProfile?.institutionId,
    redirecting: userRole === 'caregiver' || userRole === 'doctor'
  });

  // Super-admin always goes to the super-admin dashboard
  if (userRole === 'super-admin' || userProfile?.userType === 'super-admin') {
    console.log('🚀 Redirecting super-admin to /super-admin/dashboard');
    return <Navigate to="/super-admin/dashboard" replace />;
  }

  // Check if user belongs to an institution - route to institution portal
  if (userProfile?.institutionId) {
    console.log('🏢 User belongs to institution, routing to institution portal:', userProfile.institutionId);
    
    if (userRole === 'admin') {
      return <Navigate to={`/institution-admin/dashboard?institution=${userProfile.institutionId}`} replace />;
    } else if (userRole === 'pharmacist') {
      return <Navigate to={`/institution-pharmacy/dashboard?institution=${userProfile.institutionId}`} replace />;
    } else if (userRole === 'lab_technician' || userRole === 'lab-technician') {
      return <Navigate to={`/institution-lab-technician/dashboard?institution=${userProfile.institutionId}`} replace />;
    } else if (userRole === 'caregiver' || userRole === 'doctor' || userRole === 'nurse') {
      return <Navigate to={`/institution-caregiver/dashboard?institution=${userProfile.institutionId}`} replace />;
    }
  }
  
  // Check for admin session override
  const hasAdminSession = sessionStorage.getItem('Care Master_admin_session') === 'true';
  
  // Redirect admins to admin dashboard (standalone admins without institution)
  if ((userRole === 'admin' || hasAdminSession) && !userProfile?.institutionId) {
    console.log('🚀 Redirecting admin to /admin');
    return <Navigate to="/admin" replace />;
  }
  
  // Redirect caregivers and doctors to service provider dashboard (standalone without institution)
  if ((userRole === 'caregiver' || userRole === 'doctor') && !hasAdminSession && !userProfile?.institutionId) {
    console.log('🚀 Redirecting service provider to /service-provider');
    return <Navigate to="/service-provider" replace />;
  }
  
  // Default to client dashboard for elderly/client users
  console.log('✅ Showing client dashboard for role:', userRole);
  return <Layout />;
}


export default App;

// Small component that runs inside UserProvider to read context safely
function OnboardingGuardedLayout() {
  // Import hook normally; component is rendered under <UserProvider>
  const { isOnboardingIncomplete, userProfile, getCaregiverOnboardingRoute, user, loading } = useUser();
  
  console.log('🔍 OnboardingGuardedLayout Debug:', {
    user: user?.uid,
    userProfile: userProfile,
    userType: userProfile?.userType,
    isIncomplete: isOnboardingIncomplete(),
    loading: loading
  });
  
  // Show loading while user profile is being fetched
  if (loading || (user && !userProfile)) {
    console.log('⏳ Loading user profile...');
    return <LoadingSpinner />;
  }
  
  if (isOnboardingIncomplete()) {
    // IMPORTANT: Check userProfile exists before checking userType
    // Caregivers, nurses, doctors, and pharmacists use the same onboarding flow
    const caregiverTypes = ['caregiver', 'nurse', 'doctor', 'pharmacist'];
    if (userProfile && caregiverTypes.includes(userProfile.userType)) {
      const caregiverRoute = getCaregiverOnboardingRoute();
      console.log(`🔄 Redirecting ${userProfile.userType.toUpperCase()} to:`, caregiverRoute);
      return <Navigate to={caregiverRoute} replace />;
    }
    // Redirect clients/elderly to client onboarding
    console.log('🔄 Redirecting Client to onboarding, userType:', userProfile?.userType);
    return <Navigate to="/onboarding/profile" replace />;
  }
  
  console.log('✅ Onboarding complete, showing layout');
  return <Layout />;
}

// Caregiver-specific onboarding guard (also handles nurses, doctors, and pharmacists)
function CaregiverOnboardingGuard() {
  const { userProfile, getCaregiverOnboardingRoute } = useUser();
  
  // Only allow access if caregiver/nurse/doctor/pharmacist has completed onboarding
  const caregiverTypes = ['caregiver', 'nurse', 'doctor', 'pharmacist'];
  if (userProfile && caregiverTypes.includes(userProfile.userType) && !userProfile?.onboardingComplete) {
    const caregiverRoute = getCaregiverOnboardingRoute();
    console.log(`🚫 ${userProfile.userType} onboarding incomplete, redirecting to:`, caregiverRoute);
    return <Navigate to={caregiverRoute} replace />;
  }
  
  console.log(`✅ ${userProfile?.userType || 'Staff'} onboarding complete, showing caregiver layout`);
  return <CaregiverLayout />;
}

// Strict caregiver guard with immediate redirect
function StrictCaregiverGuard() {
  const { userProfile, user, loading } = useUser();
  
  // CRITICAL: If no user is authenticated, redirect to login immediately
  if (!user) {
    console.log('🚫 STRICT: No authenticated user - redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  // Show loading while profile loads
  if (loading || !userProfile) {
    return <LoadingSpinner />;
  }
  
  console.log('🔍 StrictCaregiverGuard Check:', {
    userType: userProfile?.userType,
    onboardingComplete: userProfile?.onboardingComplete,
    userId: user?.uid
  });
  
  // Allow admin users to access caregiver dashboard
  if (userProfile?.userType === 'admin') {
    console.log('✅ Admin access granted to caregiver dashboard');
    return <CaregiverLayout />;
  }
  
  // Force onboarding for incomplete caregivers, nurses, doctors, and pharmacists
  const caregiverTypes = ['caregiver', 'nurse', 'doctor', 'pharmacist'];
  if (caregiverTypes.includes(userProfile?.userType) && !userProfile?.onboardingComplete) {
    console.log(`🚫 STRICT: ${userProfile.userType} onboarding required - forcing redirect`);
    window.location.replace('/caregiver/onboarding');
    return <LoadingSpinner />;
  }
  
  // Allow access to caregiver dashboard for complete caregivers, nurses, doctors, and pharmacists
  if (caregiverTypes.includes(userProfile?.userType)) {
    console.log(`✅ ${userProfile.userType} access granted to caregiver dashboard`);
    return <CaregiverLayout />;
  }
  
  // Redirect other user types to their appropriate dashboard
  console.log('🚫 Access denied: Invalid user type for caregiver dashboard - redirecting to dashboard');
  return <Navigate to="/dashboard" replace />;
}

// Service Provider Guard - ensures only authenticated doctors and caregivers can access
function ServiceProviderGuard() {
  const { userProfile, user, loading } = useUser();
  
  // CRITICAL: If no user is authenticated, redirect to login immediately
  if (!user) {
    console.log('🚫 SERVICE PROVIDER: No authenticated user - redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  // Show loading while profile loads
  if (loading || !userProfile) {
    return <LoadingSpinner />;
  }
  
  console.log('🔍 ServiceProviderGuard Check:', {
    userType: userProfile?.userType,
    onboardingComplete: userProfile?.onboardingComplete,
    userId: user?.uid
  });
  
  // Allow admin users to access service provider dashboard
  if (userProfile?.userType === 'admin') {
    console.log('✅ Admin access granted to service provider dashboard');
    return <ServiceProviderLayout />;
  }
  
  // Allow doctors to access service provider dashboard
  if (userProfile?.userType === 'doctor') {
    console.log('✅ Doctor access granted to service provider dashboard');
    return <ServiceProviderLayout />;
  }
  
  // Allow caregivers and nurses to access service provider dashboard (after onboarding)
  if (userProfile?.userType === 'caregiver' || userProfile?.userType === 'nurse') {
    if (!userProfile?.onboardingComplete) {
      console.log('🚫 SERVICE PROVIDER: Caregiver onboarding required - redirecting to onboarding');
      window.location.replace('/caregiver/onboarding');
      return <LoadingSpinner />;
    }
    console.log('✅ Caregiver/nurse access granted to service provider dashboard');
    return <ServiceProviderLayout />;
  }
  
  // Redirect other user types to their appropriate dashboard
  console.log('🚫 Access denied: Invalid user type for service provider dashboard - redirecting to dashboard');
  return <Navigate to="/dashboard" replace />;
}
